import axios from 'axios';
import { getToken, clearAuth } from '../lib/tokenStorage';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,  // httpOnly cookie is sent automatically by the browser
});

// Fallback: also send Authorization header from localStorage for environments
// where cookies may not be available (cross-origin testing, Playwright, etc.)
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use((r) => r, (err) => {
  const url = err.config?.url || '';
  const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/forgot-password');
  if (err.response?.status === 401 && !isAuthEndpoint) {
    clearAuth();
    if (!window.location.pathname.includes('/login')) window.location.href = '/login';
  }
  return Promise.reject(err);
});

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  // Self-service username + password change. `current_password` required.
  updateMe: (body) => api.patch('/auth/me', body),
};

export const employeeAPI = {
  list: () => api.get('/employees'),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  stats: (id) => api.get(`/employees/${id}/stats`),
  analytics: (params) => api.get('/employees/analytics', { params }),
};

export const receiptAPI = {
  list: (params) => api.get('/receipts', { params }),
  get: (id) => api.get(`/receipts/${id}`),
  create: (data) => api.post('/receipts', data),
  update: (id, data) => api.put(`/receipts/${id}`, data),
  delete: (id) => api.delete(`/receipts/${id}`),
  downloadPDF: (id) => api.get(`/receipts/${id}/pdf`, { responseType: 'blob' }),
};

export const invoiceAPI = {
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  downloadPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  updateStatus: (id, status) => api.post(`/invoices/${id}/update-status`, { status }),
};

export const ledgerAPI = {
  list: (params) => api.get('/ledger', { params }),
  create: (data) => api.post('/ledger', data),
  update: (id, data) => api.put(`/ledger/${id}`, data),
  delete: (id) => api.delete(`/ledger/${id}`),
  summary: (params) => api.get('/ledger/summary', { params }),
  parties: () => api.get('/ledger/parties'),
  exportPDF: (params) => api.get('/ledger/export/pdf', { params, responseType: 'blob' }),
  exportExcel: (params) => api.get('/ledger/export/excel', { params, responseType: 'blob' }),
  import: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/ledger/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
};

export const partyAPI = {
  list: (params) => api.get('/parties', { params }),
  create: (data) => api.post('/parties', data),
  update: (id, data) => api.put(`/parties/${id}`, data),
  delete: (id) => api.delete(`/parties/${id}`),
  syncFromTransactions: () => api.post('/parties/sync-from-transactions'),
  merge: (data) => api.post('/parties/merge', data),
  findDuplicates: () => api.get('/parties/find-duplicates'),
};

export const settingsAPI = {
  getNumbering: () => api.get('/settings/numbering'),
  updateNumbering: (data) => api.put('/settings/numbering', data),
  bankAccounts: () => api.get('/settings/bank-accounts'),
  getAdvanceEngine: () => api.get('/settings/advance-engine'),
  updateAdvanceEngine: (data) => api.put('/settings/advance-engine', data),
  getAutoGst: () => api.get('/settings/auto-gst'),
  updateAutoGst: (data) => api.put('/settings/auto-gst', data),
};

export const previewAPI = {
  nextInvoiceNumber: (invoiceDate, branchId) =>
    api.get('/preview/next-invoice-number', { params: { ...(invoiceDate ? { invoice_date: invoiceDate } : {}), ...(branchId ? { branch_id: branchId } : {}) } }),
  nextReceiptNumber: (branchId) => api.get('/preview/next-receipt-number', { params: branchId ? { branch_id: branchId } : {} }),
};

export const billBookAPI = {
  list: (params) => api.get('/bill-books', { params }),
  create: (data) => api.post('/bill-books', data),
  close: (id) => api.put(`/bill-books/${id}/close`),
  dashboard: () => api.get('/bill-books/dashboard'),
  nextNumber: (branchId, bookType) => api.get('/bill-books/next-number', { params: { branch_id: branchId, book_type: bookType } }),
  requests: (params) => api.get('/bill-book-requests', { params }),
  createRequest: (data) => api.post('/bill-book-requests', data),
  approveRequest: (id, data) => api.post(`/bill-book-requests/${id}/approve`, data),
  rejectRequest: (id, data) => api.post(`/bill-book-requests/${id}/reject`, data),
};

export const fleetAPI = {
  vehicles: {
    list: (params) => api.get('/vehicles', { params }),
    get: (id) => api.get(`/vehicles/${id}`),
    create: (data) => api.post('/vehicles', data),
    update: (id, data) => api.put(`/vehicles/${id}`, data),
    remove: (id) => api.delete(`/vehicles/${id}`),
    trips: (id, params) => api.get(`/vehicles/${id}/trips`, { params }),
    exportTrips: (id, params) => api.get(`/vehicles/${id}/trips/export`, { params, responseType: 'blob' }),
  },
  drivers: {
    list: (params) => api.get('/drivers', { params }),
    get: (id) => api.get(`/drivers/${id}`),
    create: (data) => api.post('/drivers', data),
    update: (id, data) => api.put(`/drivers/${id}`, data),
    remove: (id) => api.delete(`/drivers/${id}`),
    trips: (id, params) => api.get(`/drivers/${id}/trips`, { params }),
    exportTrips: (id, params) => api.get(`/drivers/${id}/trips/export`, { params, responseType: 'blob' }),
  },
};

export const gstAPI = {
  lookup: (gstin) => api.get(`/gst/lookup/${gstin}`),
};

export const extractAPI = {
  pdf: (docType, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/extract-pdf/${docType}`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
  },
};

export const chatbotAPI = {
  send: (message) => api.post('/chatbot', { message }),
};

export const auditAPI = {
  list: (params) => api.get('/audit-logs', { params }),
};

export const dashboardAPI = {
  stats: (params) => api.get('/dashboard/stats', { params }),
  getWidgets: () => api.get('/dashboard/widgets'),
  saveWidgets: (widgets) => api.put('/dashboard/widgets', { widgets }),
  integrityCheck: () => api.get('/transactions/integrity-check'),
};

export const gstReportAPI = {
  get: (params) => api.get('/reports/gst', { params }),
};

export const statesAPI = {
  list: () => api.get('/states'),
};

export const companyAPI = {
  get: () => api.get('/company'),
};

// ===== ERP Accounting APIs =====
export const accountingAPI = {
  // Companies
  companies: () => api.get('/accounting/companies'),
  company: (id) => api.get(`/accounting/companies/${id}`),
  // Chart of Accounts
  chartOfAccounts: (params) => api.get('/accounting/chart-of-accounts', { params }),
  createAccount: (data) => api.post('/accounting/chart-of-accounts', data),
  updateAccount: (code, data) => api.put(`/accounting/chart-of-accounts/${code}`, data),
  // Journal Entries
  listJournalEntries: (params) => api.get('/accounting/journal-entries', { params }),
  getJournalEntry: (id) => api.get(`/accounting/journal-entries/${id}`),
  createJournalEntry: (data) => api.post('/accounting/journal-entries', data),
  deleteJournalEntry: (id) => api.delete(`/accounting/journal-entries/${id}`),
  // Lock/unlock voucher endpoints removed — the feature was retired.
  // Ledger
  accountLedger: (code, params) => api.get(`/accounting/ledger/${code}`, { params }),
  accountLedgerByParty: (code) => api.get(`/accounting/ledger/${code}/by-party`),
  // Reports
  trialBalance: (params) => api.get('/accounting/trial-balance', { params }),
  profitLoss: (params) => api.get('/accounting/profit-loss', { params }),
  balanceSheet: (params) => api.get('/accounting/balance-sheet', { params }),
  clientAnalytics: () => api.get('/accounting/client-analytics'),
  voucherSummary: () => api.get('/accounting/voucher-summary'),
  balanceSheetExport: (params) => api.get('/accounting/balance-sheet/export', { params }),
  uploadAttachment: (id, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/accounting/journal-entries/${id}/attachment`, fd); },
  editJournalEntry: (id, data) => api.put(`/accounting/journal-entries/${id}`, data),
  // Financial Year
  financialYears: () => api.get('/accounting/financial-years'),
  createFinancialYear: (data) => api.post('/accounting/financial-years', data),
  closeFinancialYear: (id) => api.post(`/accounting/financial-years/${id}/close`),
  reopenFinancialYear: (id) => api.post(`/accounting/financial-years/${id}/reopen`),
  // Month Locking
  monthLocks: () => api.get('/accounting/month-locks'),
  lockMonth: (month) => api.post(`/accounting/month-locks/${month}/lock`),
  unlockMonth: (month) => api.post(`/accounting/month-locks/${month}/unlock`),
  // Ledger PDF
  ledgerPdfUrl: (code, params) => {
    const qp = new URLSearchParams(params || {});
    return `${api.defaults.baseURL}/accounting/ledger/${code}/pdf?${qp.toString()}`;
  },
  // Party Ledger
  partyLedger: (name, params) => api.get(`/accounting/party-ledger/${encodeURIComponent(name)}`, { params }),
  partyList: () => api.get('/accounting/party-list'),
};

// Company Profile APIs
export const companyProfileAPI = {
  get: () => api.get('/company-profile'),
  update: (data) => api.put('/company-profile', data),
  uploadLogo: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/company-profile/logo', fd); },
};

// Bank Account APIs
export const bankAccountAPI = {
  list: () => api.get('/bank-accounts'),
  create: (data) => api.post('/bank-accounts', data),
  update: (id, data) => api.put(`/bank-accounts/${id}`, data),
  transfer: (id, data) => api.post(`/bank-accounts/${id}/transfer`, data),
  disable: (id) => api.delete(`/bank-accounts/${id}`, { headers: { 'X-Confirm-Delete': 'true' } }),
  syncFromCOA: () => api.post('/bank-accounts/sync-from-coa'),
};

// Masters APIs
export const mastersAPI = {
  list: (params) => api.get('/masters', { params }),
  create: (data) => api.post('/masters', data),
  update: (id, data) => api.put(`/masters/${id}`, data),
  delete: (id) => api.delete(`/masters/${id}`),
};

// Training APIs
export const trainingAPI = {
  list: (params) => api.get('/training', { params }),
  create: (data) => api.post('/training', data),
  uploadFile: (id, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/training/${id}/upload`, fd); },
  delete: (id) => api.delete(`/training/${id}`),
};

// Super Admin APIs
export const superAdminAPI = {
  dashboard: () => api.get('/super-admin/dashboard'),
  companies: (params) => api.get('/super-admin/companies', { params }),
  company: (id) => api.get(`/super-admin/companies/${id}`),
  createCompany: (data) => api.post('/super-admin/companies', data),
  updateCompany: (id, data) => api.put(`/super-admin/companies/${id}`, data),
  updateSubscription: (id, data) => api.put(`/super-admin/companies/${id}/subscription`, data),
  updateStatus: (id, data) => api.put(`/super-admin/companies/${id}/status`, data),
  updateFeatures: (id, data) => api.put(`/super-admin/companies/${id}/features`, data),
  deleteCompany: (id) => api.delete(`/super-admin/companies/${id}`, { headers: { 'X-Confirm-Delete': 'CONFIRM_DELETE' } }),
  purgeData: (id) => api.delete(`/super-admin/companies/${id}/data`, { headers: { 'X-Confirm-Purge': 'CONFIRM_PURGE' } }),
  resetPassword: (id, data) => api.post(`/super-admin/companies/${id}/reset-password`, data),
  subscriptionTiers: () => api.get('/super-admin/subscription-tiers'),
  featureCheck: (feature) => api.get(`/super-admin/feature-check/${feature}`),
  companyFeatures: () => api.get('/super-admin/company-features'),
};

// Leads APIs
export const leadsAPI = {
  list: (params) => api.get('/leads', { params }),
  updateStatus: (id, data) => api.patch(`/leads/${id}`, data),
  exportCSV: () => api.get('/leads/export/csv', { responseType: 'blob' }),
};

// Email Logs APIs
export const emailLogsAPI = {
  list: (params) => api.get('/email-logs', { params }),
};

// Unified Transaction APIs
export const transactionAPI = {
  list: (params) => api.get('/transactions', { params }),
  validate: (data) => api.post('/transactions/validate', data),
};

// Advance Auto-Adjust APIs
export const advanceAutoAdjustAPI = {
  settings: () => api.get('/advances/settings'),
  updateSettings: (data) => api.put('/advances/settings', data),
  autoAdjust: (partyName) => api.post(`/advances/auto-adjust/${encodeURIComponent(partyName)}`),
};

// GST Return APIs
export const gstReturnAPI = {
  gstr1: (params) => api.get('/gst-returns/gstr1', { params }),
  gstr3b: (params) => api.get('/gst-returns/gstr3b', { params }),
};

// TDS Return APIs
export const tdsReturnAPI = {
  summary: (params) => api.get('/tds-returns/summary', { params }),
  payable: (params) => api.get('/tds-returns/payable', { params }),
  receivable: (params) => api.get('/tds-returns/receivable', { params }),
};

// Global Search API
export const searchAPI = {
  search: (q) => api.get('/search', { params: { q } }),
};

export const cmsAPI = {
  get: () => api.get('/super-admin/cms'),
  update: (data) => api.put('/super-admin/cms', data),
  updateSection: (section, data) => api.put(`/super-admin/cms/section/${section}`, { data }),
};

export const notificationAPI = {
  list: (params) => api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
  vapidPublicKey: () => api.get('/notifications/vapid-public-key'),
  subscribe: (subscription) => api.post('/notifications/subscribe', { subscription }),
  unsubscribe: (endpoint) => api.post('/notifications/unsubscribe', { endpoint }),
};

export const warehouseAPI = {
  list: (params) => api.get('/warehouses', { params }),
  get: (id) => api.get(`/warehouses/${id}`),
  create: (data) => api.post('/warehouses', data),
  update: (id, data) => api.put(`/warehouses/${id}`, data),
  delete: (id) => api.delete(`/warehouses/${id}`),
};

export const blogAPI = {
  listPublic: (params) => api.get('/blog/posts', { params }),
  getPublic: (slug) => api.get(`/blog/posts/${slug}`),
  listAdmin: (params) => api.get('/blog/admin/posts', { params }),
  create: (data) => api.post('/blog/admin/posts', data),
  update: (id, data) => api.put(`/blog/admin/posts/${id}`, data),
  delete: (id) => api.delete(`/blog/admin/posts/${id}`),
};

export const gstinAPI = {
  validate: (gstin) => api.get(`/gstin/validate/${gstin}`),
};

export const featureControlAPI = {
  getCompanyFeatures: () => api.get('/super-admin/company-features'),
  checkFeature: (feature) => api.get(`/super-admin/feature-check/${feature}`),
  updateCompanyFeatures: (companyId, features) => api.put(`/super-admin/companies/${companyId}/features`, { features }),
};

export const platformAPI = {
  dataHealthSummary: () => api.get('/platform/health/data/summary'),
  dataHealthCategory: (category, limit = 200) =>
    api.get(`/platform/health/data/category/${category}`, { params: { limit } }),
  dataHealthExport: (category, format = 'json') =>
    api.get(`/platform/health/data/export/${category}`, {
      params: { format },
      responseType: 'blob',
    }),
  archiveRecords: (payload) => api.post('/platform/health/data/archive', payload),
  restoreBatch: (batchId) => api.post(`/platform/health/data/restore/${batchId}`),
  listArchiveBatches: () => api.get('/platform/health/data/archive/list'),
  systemHealth: () => api.get('/platform/health/system'),
  listClients: () => api.get('/platform/clients'),
  startReadonly: (companyId) => api.post(`/platform/clients/${companyId}/impersonate-readonly`),
  endReadonly: () => api.post('/platform/clients/readonly/end'),
  currentReadonly: () => api.get('/platform/clients/readonly/current'),
  // Consistency Engine (auto-fix)
  fixPreview: (category) => api.post(`/platform/fix/preview/${category}`),
  fixApply: (category) => api.post(`/platform/fix/apply/${category}`, { confirm: category }),
  fixOperations: () => api.get('/platform/fix/operations'),
  fixOperation: (opId) => api.get(`/platform/fix/operation/${opId}`),
  // Ledger recalculation
  ledgerRecalcDryRun: () => api.post('/platform/ledger/recalculate'),
  ledgerRecalcApply: () => api.post('/platform/ledger/recalculate?apply=true'),
  revertFixOperation: (opId) => api.post(`/platform/fix/revert/${opId}`),
  // Issues
  listIssues: (params = {}) => api.get('/issues/', { params }),
  updateIssue: (issueId, body) => api.patch(`/issues/${issueId}`, body),
  getIssue: (issueId) => api.get(`/issues/${issueId}`),
  // Danger Zone — wipe test data
  wipeTestDataPreview: () => api.get('/platform/danger/wipe-test-data/preview'),
  wipeTestData: (phrase) => api.post('/platform/danger/wipe-test-data', { confirm: phrase }),
};

export const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana",
  "Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
  "Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Andaman and Nicobar Islands","Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu","Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry"
];

export const formatCurrency = (val) => {
  const num = parseFloat(val) || 0;
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default api;
